/**
 * ClientLogin - Login & password reset for client portal
 */

import React, { useState } from 'react';
import { setTokens, setClientData, navigate } from './api';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ClientLogin({ resetMode = false }) {
  const [mode, setMode] = useState(resetMode ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetToken, setResetToken] = useState(new URLSearchParams(window.location.search).get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/client/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setTokens(data.token, data.refreshToken);
      setClientData(data.client);
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/client/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/client/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(data.message);
      setTimeout(() => { setMode('login'); setSuccess(''); }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', backgroundColor: '#0B1120', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    card: { backgroundColor: '#111827', borderRadius: 16, padding: 40, width: 420, maxWidth: '90vw', border: '1px solid #1F2937' },
    logo: { textAlign: 'center', marginBottom: 32 },
    logoText: { fontSize: 24, fontWeight: 700, color: '#3B82F6' },
    logoSub: { color: '#6B7280', fontSize: 14, marginTop: 4 },
    title: { color: '#FFF', fontSize: 20, fontWeight: 600, marginBottom: 24, textAlign: 'center' },
    field: { marginBottom: 16 },
    label: { display: 'block', color: '#9CA3AF', fontSize: 13, marginBottom: 6 },
    input: { width: '100%', padding: '12px 14px', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: 8, color: '#FFF', fontSize: 15, outline: 'none', boxSizing: 'border-box' },
    btn: { width: '100%', padding: '12px 0', backgroundColor: '#3B82F6', color: '#FFF', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
    link: { color: '#3B82F6', cursor: 'pointer', fontSize: 13, textAlign: 'center', marginTop: 16, display: 'block' },
    error: { backgroundColor: '#7F1D1D33', border: '1px solid #991B1B', padding: '10px 14px', borderRadius: 8, color: '#FCA5A5', fontSize: 13, marginBottom: 16 },
    success: { backgroundColor: '#064E3B33', border: '1px solid #065F46', padding: '10px 14px', borderRadius: 8, color: '#6EE7B7', fontSize: 13, marginBottom: 16 },
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoText}>🏢 Talk Australia</div>
          <div style={s.logoSub}>Client Portal</div>
        </div>

        {error && <div style={s.error}>{error}</div>}
        {success && <div style={s.success}>{success}</div>}

        {mode === 'login' && (
          <>
            <div style={s.title}>Sign In</div>
            <form onSubmit={handleLogin}>
              <div style={s.field}>
                <label style={s.label}>Email</label>
                <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@company.com" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Password</label>
                <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Your password" />
              </div>
              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <span style={s.link} onClick={() => setMode('forgot')}>Forgot your password?</span>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <div style={s.title}>Reset Password</div>
            <form onSubmit={handleForgotPassword}>
              <div style={s.field}>
                <label style={s.label}>Email address</label>
                <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@company.com" />
              </div>
              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <span style={s.link} onClick={() => setMode('login')}>Back to sign in</span>
          </>
        )}

        {mode === 'reset' && (
          <>
            <div style={s.title}>Set New Password</div>
            <form onSubmit={handleResetPassword}>
              <div style={s.field}>
                <label style={s.label}>Reset Token</label>
                <input style={s.input} type="text" value={resetToken} onChange={e => setResetToken(e.target.value)} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>New Password</label>
                <input style={s.input} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} placeholder="Min 8 characters" />
              </div>
              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
            <span style={s.link} onClick={() => setMode('login')}>Back to sign in</span>
          </>
        )}
      </div>
    </div>
  );
}
